/**
 * Copyright IBM Corp. 2022, 2026
 */

import classNames from 'classnames'
import type { FlightIconName, FlightIconSize } from '../flight-icon'
import { FlightIcon } from '../flight-icon'
import s from './style.module.scss'

const BADGE_SIZES = ['small', 'medium', 'large'] as const
type BadgeSize = (typeof BADGE_SIZES)[number]

const BADGE_TYPES = ['filled', 'inverted', 'outlined'] as const
type BadgeType = (typeof BADGE_TYPES)[number]

const BADGE_COLORS = [
	'neutral',
	'highlight',
	'success',
	'warning',
	'critical',
] as const
type BadgeColor = (typeof BADGE_COLORS)[number]

const ICON_SIZE_MAP = {
	small: 12,
	medium: 16,
	large: 16,
} as const satisfies Record<'small' | 'medium' | 'large', FlightIconSize>

interface BaseBadgeProps {
	size?: BadgeSize
	type?: BadgeType
	color?: BadgeColor
	className?: string
	iconColor?: string
}

interface BadgeWithTextProps extends BaseBadgeProps {
	text: string
	icon?: FlightIconName
	accessibleText?: string
}

interface IconOnlyBadgeProps extends BaseBadgeProps {
	text?: undefined
	icon: FlightIconName
	accessibleText: string
}

type BadgeProps = IconOnlyBadgeProps | BadgeWithTextProps

const Badge = ({
	icon,
	iconColor,
	text,
	size = 'medium',
	type = 'filled',
	color = 'neutral',
	className,
	accessibleText,
}: BadgeProps) => {
	return (
		<div
			className={classNames(
				s.badge,
				s[`size-${size}`],
				s[`type-${type}`],
				s[`color-${color}`],
				className,
			)}
		>
			{icon && (
				<FlightIcon name={icon} color={iconColor} size={ICON_SIZE_MAP[size]} />
			)}
			{text ? (
				<div className={s.text} aria-hidden={!!accessibleText}>
					{text}
				</div>
			) : null}
			{accessibleText ? (
				<span className="sr-only">{accessibleText}</span>
			) : null}
		</div>
	)
}

Badge.displayName = 'Badge'

export type { BadgeProps, BadgeColor, BadgeSize, BadgeType }
export { Badge, BADGE_COLORS, BADGE_SIZES, BADGE_TYPES }
